import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SEOHead from '../../components/SEO/SEOHead';

const HomePageArabic = () => {
  const { i18n } = useTranslation();
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [trialForm, setTrialForm] = useState({
    name: '',
    phone: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isContactSuccess, setIsContactSuccess] = useState(false);

  // Set language to Arabic when component mounts
  useEffect(() => {
    if (i18n.language !== 'ar') {
      i18n.changeLanguage('ar');
    }
  }, [i18n]);

  const handleTrialSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!trialForm.name.trim() || !trialForm.phone.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Submitting form data:', { name: trialForm.name, phone: trialForm.phone });
      
      const response = await fetch('https://formspree.io/f/mdkglkeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: trialForm.name.trim(),
          phone: trialForm.phone.trim(),
          message: 'طلب تجربة مجانية من الصفحة الرئيسية',
          _subject: `طلب تجربة مجانية جديد من ${trialForm.name}`,
          _captcha: false
        })
      });

      const responseData = await response.json();
      console.log('Response:', response.status, responseData);

      if (response.ok) {
        setIsSuccess(true);
        toast.success('تم إرسال طلب التجربة المجانية بنجاح! سنتواصل معك خلال 24 ساعة.');
        setTrialForm({ name: '', phone: '' });
        // Keep modal open to show success message
        setTimeout(() => {
          setIsTrialModalOpen(false);
          setIsSuccess(false);
        }, 3000);
      } else {
        throw new Error(responseData.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('حدث خطأ أثناء إرسال طلبك. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!contactForm.name.trim() || !contactForm.phone.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة.');
      return;
    }
    
    setIsContactSubmitting(true);
    
    try {
      console.log('Submitting contact form data:', contactForm);
      
      const response = await fetch('https://formspree.io/f/mdkglkeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: contactForm.name.trim(),
          phone: contactForm.phone.trim(),
          company: contactForm.company.trim(),
          message: contactForm.message.trim() || 'طلب تواصل من الصفحة الرئيسية',
          _subject: `طلب تواصل جديد من ${contactForm.name}`,
          _captcha: false
        })
      });

      const responseData = await response.json();
      console.log('Contact response:', response.status, responseData);

      if (response.ok) {
        setIsContactSuccess(true);
        toast.success('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.');
        setContactForm({ name: '', phone: '', company: '', message: '' });
      } else {
        throw new Error(responseData.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast.error('حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="ادارة اسطول | نظام ادارة اسطول - لوجستيات السعودية"
        description="نظام ادارة اسطول متكامل للمملكة العربية السعودية. برنامج ادارة اسطول شامل لتتبع الشحنات وإدارة النقل والشاحنات. احتساب تكلفة الرحلات وإدارة السائقين بأحدث تقنيات ادارة الاسطول."
        keywords="ادارة اسطول، نظام ادارة اسطول، ادارة الاسطول السعودية، برنامج ادارة اسطول، نظام ادارة الاسطول، إدارة أسطول النقل، برنامج إدارة الأسطول السعودية، نظام إدارة النقل، تتبع الشحنات، إدارة الوقود، برنامج الشاحنات، نظام النقل السعودية، ادارة المركبات، نظام ادارة المركبات"
        canonicalUrl="/"
        lang="ar"
        structuredData={{
          "@context": "https://schema.org",
          "@type": ["WebSite", "SoftwareApplication"],
          "name": "لوجستيات - نظام ادارة اسطول",
          "alternateName": ["نظام ادارة اسطول", "برنامج ادارة اسطول", "ادارة الاسطول"],
          "url": "https://logistiat.app/",
          "description": "نظام ادارة اسطول متكامل للمملكة العربية السعودية - برنامج ادارة اسطول شامل",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "2990",
            "priceCurrency": "SAR",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://logistiat.app/pricing",
            "itemCondition": "https://schema.org/NewCondition",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "SAR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "SA"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "SA",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 30,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn"
            }
          },
          "audience": {
            "@type": "Audience",
            "audienceType": "شركات النقل والشحن",
            "geographicArea": {
              "@type": "Country",
              "name": "المملكة العربية السعودية"
            }
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://logistiat.app/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-gray-900">Logistiat</Link>
            </div>
            <div className="hidden md:flex space-x-8 space-x-reverse">
              <Link to="/ar/features" className="text-gray-600 hover:text-gray-900">المميزات</Link>
              <Link to="/ar/pricing" className="text-gray-600 hover:text-gray-900">الأسعار</Link>
              <Link to="/ar/trip-costing" className="text-gray-600 hover:text-gray-900">احتساب التكلفة</Link>
              <Link to="/ar/contact" className="text-gray-600 hover:text-gray-900">اتصل بنا</Link>
              <Link to="/en" className="text-gray-600 hover:text-gray-900">English</Link>
            </div>
            <div className="flex space-x-4 space-x-reverse">
              <Link
                to="/login"
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 font-medium"
              >
                تسجيل الدخول
              </Link>
              <button
                onClick={() => setIsTrialModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                ابدأ التجربة المجانية
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                ادارة اسطول | نظام ادارة اسطول شامل
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                نظام ادارة اسطول متكامل للمملكة العربية السعودية. برنامج ادارة اسطول شامل لتتبع الشحنات، احتساب تكلفة الرحلات، إدارة الصيانة، وتسويات السائقين. حل ادارة الاسطول الأمثل للشركات السعودية.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsTrialModalOpen(true)}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
                >
                  ابدأ التجربة المجانية
                </button>
                <Link
                  to="/login"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-center"
                >
                  تسجيل الدخول للعملاء الحاليين
                </Link>
              </div>
            </div>
            
            {/* Shipment Tracking Screenshot */}
            <div className="relative">
              <div className="bg-white rounded-xl shadow-2xl p-4">
                <img
                  src="/images/shipment-tracking.png"
                  alt="لوحة تحكم تتبع الشحنات Logistiat"
                  className="rounded-lg w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                عرض مباشر
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              نظام ادارة اسطول متطور | ادارة الاسطول بذكاء
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              كل ما تحتاجه في نظام ادارة اسطول واحد - حل ادارة الاسطول الشامل لجميع عمليات النقل
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">🚛</div>
              <h3 className="text-xl font-semibold mb-3">ادارة الشحنات - نظام ادارة اسطول</h3>
              <p className="text-gray-600 mb-4">تتبع الشحنات من الاستلام إلى التسليم ضمن نظام ادارة اسطول متكامل مع تحديثات الحالة في الوقت الفعلي.</p>
              <Link to="/ar/features" className="text-blue-600 hover:text-blue-800 font-semibold">اعرف المزيد ←</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">💰</div>
              <h3 className="text-xl font-semibold mb-3">احتساب تكلفة الرحلات - ادارة اسطول</h3>
              <p className="text-gray-600 mb-4">احسب تكلفة الرحلات بدقة ضمن نظام ادارة اسطول شاملة الوقود والصيانة ومصاريف السائقين لكل كيلومتر.</p>
              <Link to="/ar/trip-costing" className="text-blue-600 hover:text-blue-800 font-semibold">اعرف المزيد ←</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-3">ادارة السائقين - نظام ادارة اسطول</h3>
              <p className="text-gray-600 mb-4">إدارة مهام السائقين والتسويات وتتبع الأداء ضمن نظام ادارة اسطول متكامل.</p>
              <Link to="/ar/features" className="text-blue-600 hover:text-blue-800 font-semibold">اعرف المزيد ←</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">⛽</div>
              <h3 className="text-xl font-semibold mb-3">تتبع الوقود - ادارة اسطول</h3>
              <p className="text-gray-600 mb-4">راقب استهلاك الوقود والتكاليف والكفاءة عبر نظام ادارة اسطول بالكامل.</p>
              <Link to="/ar/trip-costing" className="text-blue-600 hover:text-blue-800 font-semibold">اعرف المزيد ←</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-3">تقارير وتحليلات شاملة</h3>
              <p className="text-gray-600 mb-4">تقارير مفصلة عن الأداء والتكاليف لاتخاذ قرارات مدروسة.</p>
              <Link to="/ar/features" className="text-blue-600 hover:text-blue-800 font-semibold">اعرف المزيد ←</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">📱</div>
              <h3 className="text-xl font-semibold mb-3">دعم العربية والإنجليزية</h3>
              <p className="text-gray-600 mb-4">واجهة ثنائية اللغة بالكامل مصممة لمشغلي الأساطيل السعوديين.</p>
              <Link to="/en" className="text-blue-600 hover:text-blue-800 font-semibold">عرض الإنجليزية ←</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              نظام ادارة اسطول موثوق - خبرة في ادارة الاسطول
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  ★★★★★
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg">"ساعدنا نظام ادارة اسطول Logistiat في تقليل التكاليف التشغيلية بنسبة 25% من خلال تحسين تخطيط الرحلات وتتبع الوقود. أفضل حل ادارة اسطول جربناه."</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center ml-4">
                  <span className="text-blue-600 font-bold">أ</span>
                </div>
                <div>
                  <div className="font-semibold">أحمد الراشد</div>
                  <div className="text-gray-500">مدير الأسطول، شركة الرياض للنقل</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  ★★★★★
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg">"ميزة جدولة الصيانة في نظام ادارة اسطول Logistiat قضت على الأعطال المفاجئة. أصبح لدينا الآن رؤية كاملة لحالة الأسطول ويمكننا التخطيط للإصلاحات مسبقاً."</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center ml-4">
                  <span className="text-blue-600 font-bold">خ</span>
                </div>
                <div>
                  <div className="font-semibold">خالد المنصوري</div>
                  <div className="text-gray-500">مدير العمليات، الشرقية للخدمات اللوجستية</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ابدأ مع نظام ادارة اسطول Logistiat
            </h2>
            <p className="text-xl text-gray-600">
              اتصل بنا للحصول على عرض توضيحي مخصص لأفضل نظام ادارة اسطول في السعودية
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            {isContactSuccess ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">تم الإرسال بنجاح!</h4>
                <p className="text-gray-600 mb-4">
                  تم إرسال رسالتك بنجاح. سنتواصل معك قريباً.
                </p>
                <button
                  onClick={() => setIsContactSuccess(false)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  إرسال رسالة أخرى
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الاسم *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="الاسم"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الهاتف *
                    </label>
                    <input
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+966 50 000 0000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الشركة
                  </label>
                  <input
                    type="text"
                    value={contactForm.company}
                                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="اسم شركتك"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الرسالة
                  </label>
                  <textarea
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أخبرنا عن احتياجات إدارة الأسطول لديك..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isContactSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isContactSubmitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Logistiat</h3>
              <p className="text-gray-400 mb-4">نظام ادارة اسطول متكامل مصمم للمملكة العربية السعودية</p>
              <p className="text-gray-400">📞 0544288686</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">المميزات</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/ar/features">تتبع الشحنات</Link></li>
                <li><Link to="/ar/trip-costing">احتساب التكلفة</Link></li>
                <li><Link to="/ar/pricing">الأسعار</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">الشركة</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/ar/contact">اتصل بنا</Link></li>
                <li><Link to="/ar/pricing">الأسعار</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">اللغة</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/en">English</Link></li>
                <li><Link to="/">العربية</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Logistiat. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>

      {/* Free Trial Modal */}
      {isTrialModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                ابدأ تجربتك المجانية
              </h3>
              <button
                onClick={() => setIsTrialModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {isSuccess ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">تم بنجاح!</h4>
                <p className="text-gray-600 mb-4">
                  تم إرسال طلب التجربة المجانية بنجاح!
                </p>
                <p className="text-sm text-gray-500">
                  سنتواصل معك خلال 24 ساعة لإعداد حسابك.
                </p>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  احصل على وصول كامل لـ Logistiat لمدة 14 يوماً. لا حاجة لبطاقة ائتمان.
                </p>
        
                <form onSubmit={handleTrialSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      الاسم *
                    </label>
                    <input
                      type="text"
                      required
                      value={trialForm.name}
                      onChange={(e) => setTrialForm({...trialForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-right"
                      placeholder="الاسم"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      رقم الهاتف *
                    </label>
                    <input
                      type="tel"
                      required
                      value={trialForm.phone}
                      onChange={(e) => setTrialForm({...trialForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="+966 50 000 0000"
                    />
                  </div>
                  
                  <div className="flex gap-3 flex-row-reverse">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'جاري الإرسال...' : 'ابدأ التجربة'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsTrialModalOpen(false)}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      إلغاء
                    </button>
                  </div>
                </form>
        
                <p className="text-sm text-gray-500 mt-4 text-center">
                  سنتواصل معك خلال 24 ساعة لإعداد حسابك
                </p>
              </>
            )}
          </div>
        </div>
      )}
      </main>
    </>
  );
};

export default HomePageArabic;